import React, { useState, Children, Fragment, Component } from 'react';
import { PlusIcon, ChevronDownIcon, ChevronRightIcon, SaveIcon, PlusCircleIcon, TrashIcon, ClipboardListIcon } from 'lucide-react';
import { AddComponent } from './AddComponent';
import { DeleteConfirmation } from './DeleteConfirmation';
import { WorkInstructions } from './WorkInstructions';
interface RecipeDetailProps {
  recipeId: string;
  recipeName: string;
  isNewRecipe?: boolean;
}
type ComponentType = {
  id: string;
  name: string;
  department: string;
  type?: string;
  children?: ComponentType[];
  expanded?: boolean;
  isGroup?: boolean;
};
export function RecipeDetail({
  recipeId,
  recipeName,
  isNewRecipe = false
}: RecipeDetailProps) {
  const [showAddComponent, setShowAddComponent] = useState(false);
  const [selectedParentComponent, setSelectedParentComponent] = useState<ComponentType | null>(null);
  const [name, setName] = useState(recipeName);
  // Löschbestätigung
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [componentToDelete, setComponentToDelete] = useState<{
    id: string;
    parentId?: string;
    name: string;
  } | null>(null);
  // Arbeitsanweisungen
  const [showWorkInstructions, setShowWorkInstructions] = useState(false);
  // Nur vorhandene Komponenten laden, wenn es kein neues Rezept ist
  const [components, setComponents] = useState<ComponentType[]>(isNewRecipe ? [] : [{
    id: 'R061307',
    name: 'MSC Alaska Seelachs',
    department: 'APZ',
    expanded: true
  }, {
    id: 'R053408',
    name: 'Hähnchen Brust St gebraten',
    department: 'APZ',
    expanded: true
  }, {
    id: '92525',
    name: 'MI: Shrimps und Muscheln für Paella',
    department: 'Mischabteilung',
    isGroup: true,
    expanded: true,
    children: [{
      id: 'R061304',
      name: 'MSC Miesmuschel',
      department: 'APZ'
    }, {
      id: 'R064375',
      name: 'MSC Kaltwassergarnelen',
      department: 'APZ'
    }]
  }, {
    id: '92437',
    name: 'MI: Reis und Gemüse für Paella PL',
    department: 'Mischabteilung',
    isGroup: true,
    expanded: true,
    children: [{
      id: 'R161215',
      name: 'Langkornreis',
      department: 'APZ'
    }, {
      id: 'R090533',
      name: 'Erbsen',
      department: 'APZ'
    }, {
      id: 'R092017',
      name: 'Tomate',
      department: 'APZ'
    }, {
      id: 'R092616',
      name: 'Zwiebeln',
      department: 'APZ'
    }, {
      id: 'R091606',
      name: 'Paprika',
      department: 'APZ'
    }, {
      id: 'R099035',
      name: 'Zuckererbsen',
      department: 'APZ'
    }, {
      id: 'R151501',
      name: 'Rapsöl raffiniert',
      department: 'APZ'
    }]
  }, {
    id: 'K050698',
    name: '> Flüssigwürzung Paella PL',
    department: 'Kochabteilung',
    isGroup: true,
    expanded: true,
    children: [{
      id: 'R192313',
      name: 'WASSER',
      department: 'APZ'
    }, {
      id: 'T050041',
      name: 'GEW: Gewürze für K050698',
      department: 'Gewürzküche',
      isGroup: true,
      expanded: true,
      children: [{
        id: 'R140628',
        name: 'Fond Fisch',
        department: 'GKNass'
      }, {
        id: 'R140666',
        name: 'Fond',
        department: 'GKNass'
      }, {
        id: 'R142015',
        name: 'Tomatenmark',
        department: 'GKNass'
      }, {
        id: 'R142608',
        name: 'Zwiebeln',
        department: 'GKTrocken'
      }, {
        id: 'R141117',
        name: 'xxx',
        department: 'GKTrocken'
      }, {
        id: 'R141616',
        name: 'Pfeffer',
        department: 'GKTrocken'
      }, {
        id: 'R145521',
        name: 'xxx',
        department: 'GKTrocken'
      }, {
        id: 'R140308',
        name: 'xxx',
        department: 'GKTrocken'
      }, {
        id: 'R145544',
        name: 'xxx',
        department: 'GKTrocken'
      }, {
        id: 'R141931',
        name: 'Salz',
        department: 'GKTrocken'
      }]
    }, {
      id: 'R091176',
      name: 'Knoblauchpüree Pellets',
      department: 'APZ'
    }, {
      id: 'R092014',
      name: 'Thymian',
      department: 'APZ'
    }, {
      id: 'R193001',
      name: 'Stickstoff flüssig',
      department: 'APZ'
    }]
  }]);
  // Komponente zur Gruppe hinzufügen
  const addComponentToGroup = (parentId: string, newComponent: ComponentType) => {
    const updateComponentsRecursive = (items: ComponentType[]): ComponentType[] => {
      return items.map(item => {
        if (item.id === parentId) {
          return {
            ...item,
            children: [...(item.children || []), newComponent],
            expanded: true // Automatisch aufklappen
          };
        }
        if (item.children) {
          return {
            ...item,
            children: updateComponentsRecursive(item.children)
          };
        }
        return item;
      });
    };
    setComponents(updateComponentsRecursive(components));
  };
  // Komponente löschen (sowohl auf oberster Ebene als auch in verschachtelten Gruppen)
  const deleteComponent = (componentId: string, parentId?: string) => {
    if (!parentId) {
      // Löschen einer Komponente auf oberster Ebene
      setComponents(components.filter(component => component.id !== componentId));
    } else {
      // Löschen einer Unterkomponente
      const updateComponentsRecursive = (items: ComponentType[]): ComponentType[] => {
        return items.map(item => {
          // Wenn dies die übergeordnete Komponente ist, filtern wir die zu löschende Komponente heraus
          if (item.id === parentId) {
            return {
              ...item,
              children: item.children?.filter(child => child.id !== componentId) || []
            };
          }
          // Wenn diese Komponente Kinder hat, durchsuchen wir sie rekursiv
          if (item.children) {
            return {
              ...item,
              children: updateComponentsRecursive(item.children)
            };
          }
          return item;
        });
      };
      setComponents(updateComponentsRecursive(components));
    }
    setShowDeleteConfirmation(false);
    setComponentToDelete(null);
  };
  // Löschvorgang starten
  const handleDeleteComponent = (component: ComponentType, parentId?: string) => {
    setComponentToDelete({
      id: component.id,
      parentId,
      name: component.name
    });
    setShowDeleteConfirmation(true);
  };
  const toggleExpand = (componentId: string) => {
    const updateExpandState = (items: ComponentType[]): ComponentType[] => {
      return items.map(item => {
        if (item.id === componentId) {
          return {
            ...item,
            expanded: !item.expanded
          };
        }
        if (item.children) {
          return {
            ...item,
            children: updateExpandState(item.children)
          };
        }
        return item;
      });
    };
    setComponents(updateExpandState(components));
  };
  const handleAddToGroup = (component: ComponentType) => {
    setSelectedParentComponent(component);
    setShowAddComponent(true);
  };
  // Funktion zum Finden der übergeordneten Komponente
  const findParentDepartment = (components: ComponentType[], childId: string, parentId?: string): string | null => {
    if (parentId) {
      for (const component of components) {
        if (component.id === parentId) {
          return component.department;
        }
        if (component.children) {
          const foundInChildren = findParentDepartment(component.children, childId, parentId);
          if (foundInChildren) return foundInChildren;
        }
      }
    }
    return null;
  };
  const renderComponent = (component: ComponentType, level: number = 0, isLast: boolean = false, parentId?: string) => {
    const hasChildren = component.children && component.children.length > 0;
    // Finde die Abteilung der übergeordneten Komponente
    const parentDepartment = parentId ? findParentDepartment(components, component.id, parentId) : null;
    // Bestimme die Hintergrundfarbe basierend auf Abteilung und übergeordneter Komponente
    let bgColor = 'bg-white';
    let textColor = 'text-gray-800';
    // Hauptabteilungen - modernere, subtilere Farben
    if (component.department === 'Mischabteilung') {
      bgColor = 'bg-indigo-500 bg-opacity-85';
      textColor = 'text-white';
    } else if (component.department === 'Kochabteilung') {
      bgColor = 'bg-violet-500 bg-opacity-85';
      textColor = 'text-white';
    } else if (component.department === 'Gewürzküche') {
      bgColor = 'bg-emerald-500 bg-opacity-85';
      textColor = 'text-white';
    }
    // Unterabteilungen der Gewürzküche - sanftere Farben
    else if (component.department === 'GKNass') {
      bgColor = 'bg-emerald-100';
      textColor = 'text-emerald-900';
    } else if (component.department === 'GKTrocken') {
      bgColor = 'bg-rose-100';
      textColor = 'text-rose-900';
    }
    // APZ-Rohstoffe mit unterschiedlichen Farben je nach übergeordneter Komponente
    else if (component.department === 'APZ') {
      if (parentDepartment === 'Mischabteilung') {
        bgColor = 'bg-indigo-50';
        textColor = 'text-indigo-900';
      } else if (parentDepartment === 'Kochabteilung') {
        bgColor = 'bg-violet-50';
        textColor = 'text-violet-900';
      } else {
        bgColor = 'bg-gray-50';
        textColor = 'text-gray-800';
      }
    }
    // Prüfen, ob die Komponente ein Plus-Button haben sollte
    const showAddButton = component.isGroup || component.department === 'Kochabteilung' || component.department === 'Mischabteilung' || component.department === 'Gewürzküche';
    return <Fragment key={component.id} data-id="element-1311">
        <tr className={`${bgColor} ${textColor} hover:bg-opacity-90 transition-colors`} data-id="element-1312">
          <td className="w-10 px-2 py-2 border-b border-gray-200" data-id="element-1313">
            {hasChildren ? <button onClick={() => toggleExpand(component.id)} className="p-1" data-id="element-1314">
                {component.expanded ? <ChevronDownIcon size={16} data-id="element-1315" /> : <ChevronRightIcon size={16} data-id="element-1316" />}
              </button> : <div className="pl-6" data-id="element-1317"></div>}
          </td>
          <td className="px-2 py-2 border-b border-gray-200 text-sm" data-id="element-1318">
            <div style={{
            paddingLeft: `${level * 20}px`
          }} className="flex items-center" data-id="element-1319">
              <span data-id="element-1320">{component.id}</span>
            </div>
          </td>
          <td className="px-4 py-2 border-b border-gray-200 text-sm" data-id="element-1321">
            {component.name}
          </td>
          <td className="px-4 py-2 border-b border-gray-200 text-sm text-right" data-id="element-1322">
            {component.department}
          </td>
          <td className="px-2 py-2 border-b border-gray-200 text-sm text-right" data-id="element-1323">
            <div className="flex justify-end space-x-2" data-id="element-1324">
              {showAddButton && <button onClick={() => handleAddToGroup(component)} className="p-1 text-white hover:text-gray-100 transition-colors" title="Unterkomponente hinzufügen" data-id="element-1325">
                  <PlusCircleIcon size={16} data-id="element-1326" />
                </button>}
              <button onClick={() => handleDeleteComponent(component, parentId)} className="p-1 text-red-500 hover:text-red-700 transition-colors" title="Komponente löschen" data-id="element-1327">
                <TrashIcon size={16} data-id="element-1328" />
              </button>
            </div>
          </td>
        </tr>
        {hasChildren && component.expanded && component.children!.map((child, index) => renderComponent(child, level + 1, index === component.children!.length - 1, component.id))}
      </Fragment>;
  };
  return <div className="bg-white rounded-xl shadow-md p-6" data-id="element-1329">
      <div className="flex justify-between items-center mb-6" data-id="element-1330">
        <div data-id="element-1331">
          <h3 className="text-xl font-bold text-gray-800" data-id="element-1332">
            {recipeId} - {name}
          </h3>
          <p className="text-gray-600" data-id="element-1333">Rezeptdetails und Komponenten</p>
        </div>
        <div className="flex gap-2" data-id="element-1334">
          <button onClick={() => setShowWorkInstructions(true)} className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors" data-id="element-1335">
            <ClipboardListIcon size={18} data-id="element-1336" />
            <span data-id="element-1337">Arbeitsanweisungen</span>
          </button>
          <button className="flex items-center gap-2 bg-[#8DC63F] hover:bg-[#7AB929] text-white px-4 py-2 rounded-lg transition-colors" data-id="element-1338">
            <SaveIcon size={18} data-id="element-1339" />
            <span data-id="element-1340">Speichern</span>
          </button>
        </div>
      </div>
      <div className="mb-6" data-id="element-1341">
        <div className="grid grid-cols-2 gap-4" data-id="element-1342">
          <div data-id="element-1343">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1344">
              Rezept-Nr.
            </label>
            <input type="text" className="w-full p-2 border border-gray-300 rounded-md" value={recipeId} readOnly data-id="element-1345" />
          </div>
          <div data-id="element-1346">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1347">
              Bezeichnung
            </label>
            <input type="text" className="w-full p-2 border border-gray-300 rounded-md" value={name} onChange={e => setName(e.target.value)} data-id="element-1348" />
          </div>
        </div>
      </div>
      <div className="mb-4 flex justify-between items-center" data-id="element-1349">
        <h4 className="text-lg font-semibold text-gray-800" data-id="element-1350">
          Rezeptkomponenten
        </h4>
        <button onClick={() => {
        setSelectedParentComponent(null);
        setShowAddComponent(true);
      }} className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm transition-colors" data-id="element-1351">
          <PlusIcon size={16} data-id="element-1352" />
          <span data-id="element-1353">Komponente hinzufügen</span>
        </button>
      </div>
      <div className="mb-3 flex items-center gap-3 flex-wrap" data-id="element-1354">
        <div className="px-3 py-1 bg-indigo-500 bg-opacity-85 text-xs text-white rounded-md shadow-sm" data-id="element-1355">
          Mischabteilung
        </div>
        <div className="px-3 py-1 bg-indigo-50 text-xs text-indigo-900 rounded-md shadow-sm" data-id="element-1356">
          APZ unter Mischabteilung
        </div>
        <div className="px-3 py-1 bg-violet-500 bg-opacity-85 text-xs text-white rounded-md shadow-sm" data-id="element-1357">
          Kochabteilung
        </div>
        <div className="px-3 py-1 bg-violet-50 text-xs text-violet-900 rounded-md shadow-sm" data-id="element-1358">
          APZ unter Kochabteilung
        </div>
        <div className="px-3 py-1 bg-emerald-500 bg-opacity-85 text-xs text-white rounded-md shadow-sm" data-id="element-1359">
          Gewürzküche
        </div>
        <div className="px-3 py-1 bg-emerald-100 text-xs text-emerald-900 rounded-md shadow-sm" data-id="element-1360">
          GKNass
        </div>
        <div className="px-3 py-1 bg-rose-100 text-xs text-rose-900 rounded-md shadow-sm" data-id="element-1361">
          GKTrocken
        </div>
        <div className="px-3 py-1 bg-gray-50 text-xs text-gray-800 rounded-md shadow-sm border border-gray-200" data-id="element-1362">
          APZ
        </div>
      </div>
      <div className="overflow-hidden border border-gray-200 rounded-lg mb-4" data-id="element-1363">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-1364">
          <thead className="bg-gray-50" data-id="element-1365">
            <tr data-id="element-1366">
              <th scope="col" className="w-10 px-2 py-3" data-id="element-1367"></th>
              <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1368">
                Artikel-Nr.
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1369">
                Bezeichnung
              </th>
              <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1370">
                Abteilung
              </th>
              <th scope="col" className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1371">
                Aktion
              </th>
            </tr>
          </thead>
          <tbody data-id="element-1372">
            {components.length > 0 ? components.map((component, index) => renderComponent(component, 0, index === components.length - 1)) : <tr data-id="element-1373">
                <td colSpan={5} className="px-6 py-4 text-center text-gray-500" data-id="element-1374">
                  Keine Komponenten vorhanden. Fügen Sie Komponenten mit dem
                  Button "Komponente hinzufügen" hinzu.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
      {showAddComponent && <AddComponent onAdd={component => {
      if (selectedParentComponent) {
        // Zu einer Gruppe hinzufügen
        addComponentToGroup(selectedParentComponent.id, component);
      } else {
        // Zur Hauptliste hinzufügen
        setComponents([...components, component]);
      }
      setShowAddComponent(false);
      setSelectedParentComponent(null);
    }} onCancel={() => {
      setShowAddComponent(false);
      setSelectedParentComponent(null);
    }} parentComponent={selectedParentComponent} existingComponents={components} data-id="element-1375" />}
      {showDeleteConfirmation && componentToDelete && <DeleteConfirmation componentName={componentToDelete.name} onConfirm={() => deleteComponent(componentToDelete.id, componentToDelete.parentId)} onCancel={() => {
      setShowDeleteConfirmation(false);
      setComponentToDelete(null);
    }} data-id="element-1376" />}
      {showWorkInstructions && <WorkInstructions recipeId={recipeId} recipeName={name} onClose={() => setShowWorkInstructions(false)} data-id="element-1377" />}
    </div>;
}